import React from 'react';
import { BookingPill } from '../../BookingPill';
import { StatusMenu } from '../StatusMenu';
import {
  hoursForDate,
  applyDateException,
  formatWallMin,
} from '../../../lib/availability';
import {
  dateKeyOf,
  parseWallToMin,
  ghostSlotsFor,
  colourFor,
  statusClassFor,
  fmtRange,
  CARD_INSET_PX,
} from '../../../lib/diary/helpers';

// ===== BY BARBER VIEW =====
export const ByBarberView = ({
  date, barbers, hoursByBarber, exceptionsByBarber, breaksByBarber,
  bookings, hours, rowHeights, totalHeight, topForMin,
  scrollRef, eventStyle, isFirstForCustomer, nowTop, onClickGhostSlot,
  onStatusChanged, onOpenBooking, isBarber, myBarberId, myPerms, focusBookingId,
}) => {
  const cols = barbers.length || 1;
  const dKey = dateKeyOf(date);
  return (
    <div className="cal" data-testid="cal-by-barber" ref={scrollRef}>
      <div className="cal-head" style={{ gridTemplateColumns: `56px repeat(${cols}, 1fr)` }}>
        <div></div>
        {barbers.map(b => (
          <div key={b.id}>
            <span className="bd">{b.name.charAt(0)}</span>{b.name.split(' ')[0]}
          </div>
        ))}
        {barbers.length === 0 && <div style={{ color: 'var(--mute)' }}>No barbers</div>}
      </div>
      <div className="cal-grid" style={{ gridTemplateColumns: `56px repeat(${cols}, 1fr)` }}>
        <div className="hours">
          {hours.map((h, i) => (
            <div key={h} className="hr" style={{ height: `${rowHeights[i]}px` }}>{h}</div>
          ))}
        </div>
        {barbers.map(b => {
          const mine = bookings.filter(x => x.barber_id === b.id);
          const weekly = hoursForDate(hoursByBarber, b.id, date);
          const exc = exceptionsByBarber?.[b.id]?.[dKey];
          const h = applyDateException(weekly, exc);
          const breaksToday = (breaksByBarber?.[b.id]?.[dKey]) || [];
          const ghosts = h.open
            ? ghostSlotsFor(b.id, date, hoursByBarber, mine, breaksToday, exceptionsByBarber?.[b.id], b.booking_buffer_minutes || 0)
            : [];
          const workingTop = h.open ? topForMin(h.startMin) : 0;
          const workingHeight = h.open ? topForMin(h.endMin) - topForMin(h.startMin) : 0;
          // Distinguish "closed today because of a date exception" from
          // "closed on this weekday" so the label reads accurately.
          const closedLabel = !h.open
            ? (exc?.is_closed ? 'Day off' : 'Closed')
            : null;
          return (
            <div key={b.id} className="laneset" data-testid={`lane-${b.id}`}>
              <div className="lane-bg">
                {hours.map((hr, i) => (
                  <div key={hr} className="slot" style={{ height: `${rowHeights[i]}px` }} />
                ))}
                {/* Closed band overlay: above working hours */}
                {(!h.open || (h.open && workingTop > 0)) && (
                  <div
                    className="slot closed"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0, right: 0,
                      height: h.open ? `${workingTop}px` : '100%',
                      border: 'none',
                    }}
                    data-testid={`lane-closed-top-${b.id}`}
                  />
                )}
                {/* Closed band overlay: below working hours (if open) */}
                {h.open && workingTop + workingHeight < totalHeight && (
                  <div
                    className="slot closed"
                    style={{
                      position: 'absolute',
                      top: `${workingTop + workingHeight}px`,
                      left: 0, right: 0,
                      bottom: 0,
                      border: 'none',
                    }}
                    data-testid={`lane-closed-bottom-${b.id}`}
                  />
                )}
                {/* Closed-day full-lane label */}
                {!h.open && (
                  <div style={{
                    position: 'absolute',
                    top: 12, left: 0, right: 0,
                    textAlign: 'center',
                    fontSize: 11,
                    color: 'var(--mute)',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    pointerEvents: 'none',
                  }} data-testid={`lane-closed-label-${b.id}`}>
                    {closedLabel}
                  </div>
                )}
                {/* Break overlays (only when barber is otherwise open) */}
                {h.open && breaksToday.map((br, i) => {
                  const s = parseWallToMin(br.start_time);
                  const e = parseWallToMin(br.end_time);
                  if (s == null || e == null || e <= s) return null;
                  const top = topForMin(s);
                  const height = Math.max(14, topForMin(e) - top - CARD_INSET_PX);
                  return (
                    <div
                      key={`break-${b.id}-${i}-${br.start_time}`}
                      className="slot on-break"
                      style={{
                        position: 'absolute',
                        top: `${top}px`,
                        left: 0, right: 0,
                        height: `${height}px`,
                        pointerEvents: 'none',
                      }}
                      title={`${br.label || 'Break'} · ${formatWallMin(s)} – ${formatWallMin(e)}`}
                      data-testid={`lane-break-${b.id}-${s}`}
                    >
                      <span className="on-break-label">
                        {br.label || 'Break'}
                      </span>
                    </div>
                  );
                })}
                {/* Ghost slots (available and clickable) */}
                {ghosts.map(g => (
                  <div
                    key={`ghost-${b.id}-${g.startMin}`}
                    className="slot ghost"
                    style={{
                      top: `${topForMin(g.startMin)}px`,
                      height: `${topForMin(g.endMin) - topForMin(g.startMin) - CARD_INSET_PX}px`,
                    }}
                    onClick={() => onClickGhostSlot(b.id, g.startMin)}
                    title={`Book ${b.name.split(' ')[0]} at ${formatWallMin(g.startMin)}`}
                    data-testid={`ghost-${b.id}-${g.startMin}`}
                  />
                ))}
              </div>
              {mine.map(bk => {
                const nowTs = Date.now();
                const isRunning =
                  new Date(bk.starts_at).getTime() <= nowTs &&
                  new Date(bk.ends_at).getTime()   >  nowTs;
                const stl = eventStyle(bk);
                const hpx = Number(String(stl.height || '0').replace('px','')) || 0;
                const tight = hpx < 60 ? ' ev-tight' : '';
                const verytight = hpx < 40 ? ' ev-verytight' : '';
                return (
                  <div
                    key={bk.id}
                    className={`ev${tight}${verytight} ${colourFor(bk, isFirstForCustomer(bk))}${focusBookingId === bk.id ? ' ev-focus' : ''}${isRunning ? ' running' : ''} ${statusClassFor(bk)}`}
                    style={stl}
                    data-testid={`ev-${bk.id}`}
                    title={`${bk.customer?.name} · ${bk.service?.name}`}
                    onClick={() => onOpenBooking?.(bk.id)}
                  >
                    <div className="ev-body">
                      <span className="ev-time">{fmtRange(bk, formatWallMin)}</span>
                      <b className="ev-name">{bk.customer?.name}</b>
                      <small className="ev-svc">{bk.service?.name}</small>
                    </div>
                    <div className="ev-aside">
                      <BookingPill booking={bk} testIdSuffix={bk.id} />
                      <StatusMenu
                        booking={bk}
                        onChanged={onStatusChanged}
                        canManage={!isBarber || bk.barber_id === myBarberId}
                        perms={isBarber ? myPerms : undefined}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {nowTop !== null && (
        <div className="nowline" style={{ top: nowTop + 41 }} data-testid="cal-nowline" />
      )}
    </div>
  );
};
